import React from 'react';
import { CheckCircleIcon } from '@heroicons/react/24/outline';
import type { ResumoDivergencia } from '../../types/conferencia';
import { statusItemConferencia, type ToneConferencia } from '../../utils/statusConferencia';

// Mesmo TONE_BADGE das demais telas Íris (ver XmlTerceirosList.tsx / statusConferencia.ts).
const TONE_BADGE = {
  ok: 'bg-ok-soft text-ok',
  warning: 'bg-warning-soft text-warning',
  danger: 'bg-danger-soft text-danger',
  accent: 'bg-accent-soft text-accent-text',
  neutral: 'bg-surface-2 text-tx-3',
} as const;

interface DivergenciaResumoProps {
  resumo: ResumoDivergencia;
  className?: string;
}

/**
 * Badges compactas do resumo de divergência (falta/excedente/avaria) — usado na linha/card
 * da lista (Task 13) e reaproveitado ao vivo no workspace de bipagem (Task 15), por isso
 * recebe o `ResumoDivergencia` já calculado em vez de recalcular por conta própria.
 */
const DivergenciaResumo: React.FC<DivergenciaResumoProps> = ({ resumo, className = '' }) => {
  const badges: { key: string; label: string; tone: ToneConferencia }[] = [];
  if (resumo.falta > 0) badges.push({ key: 'falta', label: `${resumo.falta} falta`, tone: statusItemConferencia('falta').tone });
  if (resumo.excedente > 0) badges.push({ key: 'excedente', label: `${resumo.excedente} exced.`, tone: statusItemConferencia('excedente').tone });
  if (resumo.avaria > 0) badges.push({ key: 'avaria', label: `${resumo.avaria} avaria`, tone: statusItemConferencia('avaria').tone });

  if (badges.length > 0) {
    return (
      <div className={`flex flex-wrap items-center gap-1 ${className}`}>
        {badges.map((b) => (
          <span key={b.key} className={`inline-flex rounded-full px-2 py-0.5 text-[11px] font-medium ${TONE_BADGE[b.tone]}`}>
            {b.label}
          </span>
        ))}
      </div>
    );
  }

  // Sem divergência: distingue "ainda sem item bipado" de "tudo conferido e batendo".
  if (resumo.total === 0) {
    return <span className={`text-[11px] text-tx-3 ${className}`}>Sem itens</span>;
  }
  if (resumo.pendentes > 0) {
    return (
      <span className={`inline-flex rounded-full px-2 py-0.5 text-[11px] font-medium ${TONE_BADGE.neutral} ${className}`}>
        {resumo.pendentes} pendente{resumo.pendentes > 1 ? 's' : ''}
      </span>
    );
  }
  return (
    <span className={`inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-[11px] font-medium ${TONE_BADGE[statusItemConferencia('ok').tone]} ${className}`}>
      <CheckCircleIcon className="h-3 w-3" /> OK
    </span>
  );
};

export default DivergenciaResumo;
